import { useState, useRef, useEffect } from "react"
import { useSnapshot } from "valtio"
import type { NodeValues, CreatedTreeState, TreeNode } from "./types"

type UseFoldNodesOptions<T> = {
  subset?: string[] | null
  filter?: (nodeId: string, nodeValues: NodeValues<T> | undefined) => boolean
  filterActive?: boolean
}
export function useFoldNodes<T>(
  ts: CreatedTreeState<T>,
  opts: UseFoldNodesOptions<T>,
): string[] {
  const { subset, filter, filterActive } = opts
  const stateSnap = useSnapshot(ts.state)
  const treeSnap = useSnapshot(ts.tree)
  const expandedSnap = useSnapshot(ts.expanded)

  const root = treeSnap["ROOT"]
  if (!root) {
    return []
  }

  const isRootNode = (node: TreeNode) => Boolean(node.id === "ROOT")
  const hasChildren = (node: TreeNode) => Boolean(node.children)
  const isExpanded = (node: TreeNode) =>
    isRootNode(node) || Boolean(expandedSnap[node.id])
  const isInSubset = (id: string) => {
    return subset == null || subset.includes(id)
  }
  const passFilter = (node: TreeNode) => {
    if (filter === undefined || !filterActive) return true
    const nodeValues = stateSnap[node.id] as NodeValues<T>
    return filter(node.id, nodeValues)
  }

  const anyMatch = (ids: string[]) => {
    return ids.some((id) => {
      if (!isInSubset(id)) return false
      const node: TreeNode = treeSnap[id]
      return passFilter(node)
    })
  }

  const anyOfAllMyChildrenPassedTheGreatFilter = (id: string) => {
    const ids = ts.getAllChildren(id)
    return anyMatch(ids)
  }

  const includeChildren = (node: TreeNode) => {
    return (
      isExpanded(node) ||
      Boolean(filterActive && anyOfAllMyChildrenPassedTheGreatFilter(node.id))
    )
  }

  function fold<Acc>(
    fn: (acc: Acc, next: TreeNode) => Acc,
    init: Acc,
    node: TreeNode,
  ): Acc {
    if (!node) return init
    const acc = fn(init, node)
    if (!hasChildren(node)) return acc
    if (!includeChildren(node)) {
      return acc
    }
    return node.children!.reduce((acc, id) => fold(fn, acc, treeSnap[id]), acc)
  }

  const append = (ls: string[], node: TreeNode) => {
    if (isRootNode(node)) return ls
    if (!isInSubset(node.id)) return ls
    if (!passFilter(node)) return ls
    ls.push(node.id)
    return ls
  }

  return fold<string[]>(append, [], root)
}

export function keysChanged(xs: string[], ys: string[]) {
  if (xs.length !== ys.length) return true
  return xs.some((x, i) => x !== ys[i])
}

export function useNodesChanged(keys: string[]) {
  const prev = useRef<string[]>(keys)
  const [count, setCount] = useState<number>(() => 0)
  useEffect(() => {
    const changed = keysChanged(keys, prev.current)
    if (changed) {
      prev.current = keys
      setCount((s) => s + 1)
    }
  }, [keys])

  return count
}
